import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const drawerVariants = {
  closed: {
    x: "100%",
    transition: { duration: 0.4, ease: [0.7, 0, 0.2, 1] },
  },
  open: {
    x: 0,
    transition: {
      duration: 0.5,
      ease: EASE,
      staggerChildren: 0.06,
      delayChildren: 0.18,
    },
  },
};

const itemVariants = {
  closed: { x: 48, opacity: 0, transition: { duration: 0.2 } },
  open: { x: 0, opacity: 1, transition: { duration: 0.45, ease: EASE } },
};

function MobileMenu({
  isOpen,
  setIsOpen,
  navLinks,
  link,
  t,
  logoIcon,
  languages = [],
  currentLang,
  onChangeLang,
}) {
  const location = useLocation();

  const close = () => setIsOpen(false);

  return (
    <>
      {/* Hamburger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative z-[999] flex w-[32px] h-[32px] items-center justify-center cursor-my"
        aria-label="Menu"
      >
        <span
          className={`absolute block w-[22px] h-[1.5px] bg-white rounded-full transition-all duration-300 origin-center ${
            isOpen ? "rotate-45 translate-y-0" : "-translate-y-[5px]"
          }`}
        />
        <span
          className={`absolute block w-[22px] h-[1.5px] bg-white rounded-full transition-all duration-300 ${
            isOpen ? "opacity-0 scale-x-0" : ""
          }`}
        />
        <span
          className={`absolute block w-[22px] h-[1.5px] bg-white rounded-full transition-all duration-300 origin-center ${
            isOpen ? "-rotate-45 translate-y-0" : "translate-y-[5px]"
          }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              className="fixed inset-0 z-[90] bg-[rgba(0,0,0,0.6)] backdrop-blur-[6px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={close}
            />

            {/* Drawer */}
            <motion.div
              className="fixed top-0 right-0 bottom-0 w-full sm:w-[400px] bg-[#020202] z-[95] flex flex-col overflow-hidden"
              variants={drawerVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              {/* Декор: оранжевое свечение в углу */}
              <div
                className="absolute -top-[120px] -right-[120px] w-[320px] h-[320px] rounded-full bg-[rgba(238,84,20,0.22)] blur-[110px] pointer-events-none"
                aria-hidden="true"
              />

              <div className="relative flex items-center justify-between px-[24px] pt-[24px] pb-[28px]">
                <Link to="/" onClick={close}>
                  <img src={logoIcon} alt="Treefield Partners AG" className="w-[32px] h-[32px] object-contain" />
                </Link>
                <div className="w-[32px] h-[32px]" aria-hidden="true" />
              </div>

              <nav className="relative flex flex-col px-[24px] flex-1 overflow-y-auto">
                {navLinks.map((item, index) => (
                  <motion.div key={item.to} variants={itemVariants}>
                    <Link
                      to={item.to}
                      onClick={close}
                      className="group flex items-baseline gap-[14px] py-[16px] border-b border-[rgba(255,255,255,0.06)]"
                    >
                      <span className="ibm-plex text-[12px] text-[#ee5414] shrink-0">
                        0{index + 1}
                      </span>
                      <span
                        className={`font-['Montserrat'] font-[800] text-[26px] uppercase leading-none transition-all duration-300 group-hover:translate-x-[6px] ${
                          location.pathname === item.to
                            ? "text-white"
                            : "text-[#636363] group-hover:text-white"
                        }`}
                      >
                        {item.label}
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </nav>

              <motion.div className="relative px-[24px] pt-[24px]" variants={itemVariants}>
                <p className="font-['Inter'] text-[12px] uppercase tracking-[1.4px] text-[#636363] mb-[14px]">
                  {t("header.language")}
                </p>
                <div className="grid grid-cols-3 gap-[8px]">
                  {languages.map((language) => (
                    <button
                      key={language.value}
                      type="button"
                      onClick={() => onChangeLang(language)}
                      className={`h-[42px] rounded-[12px] border font-['Inter'] text-[13px] font-medium uppercase tracking-[1.4px] transition-all duration-200 cursor-my active:scale-[0.96] ${
                        currentLang === language.value
                          ? "border-[#ee5414] bg-[rgba(238,84,20,0.18)] text-[#ee5414]"
                          : "border-[rgba(255,255,255,0.08)] text-[#8f8f8f] hover:border-[rgba(255,255,255,0.25)] hover:text-white"
                      }`}
                    >
                      {language.label}
                    </button>
                  ))}
                </div>
              </motion.div>

              <motion.div className="relative px-[24px] pb-[40px] pt-[24px]" variants={itemVariants}>
                <a
                  href={link}
                  onClick={close}
                  className="flex items-center justify-center h-[48px] rounded-[60px] bg-[#ee5414] font-['Inter'] font-medium text-[16px] text-white uppercase w-full hover:opacity-90 transition-opacity cursor-my"
                >
                  {t("header.6")}
                </a>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default MobileMenu;
